import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleNotchIcon, MagnifyingGlass } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import { useGetCompanyUsers } from '../../services/users.service';
import { useAddMembersToGroup } from '../../services/groups.service';
import type { UserCompanyMemberDto } from '@monorepo/shared';

interface AddMemberToGroupModalProps {
  companyId: string;
  groupId: string;
  existingMemberIds: string[];
  onSuccess: () => void;
}

export interface AddMemberToGroupModalRef {
  open: () => void;
}

export const AddMemberToGroupModal = forwardRef<
  AddMemberToGroupModalRef,
  AddMemberToGroupModalProps
>(({ companyId, groupId, existingMemberIds, onSuccess }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  const { data: usersData, isLoading } = useGetCompanyUsers(companyId, 1, 100, debouncedSearch);

  const addMembersMutation = useAddMembersToGroup(companyId);

  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      setSearch('');
      setSelectedUserIds(new Set());
    },
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const availableUsers =
    usersData?.items.filter((user) => !existingMemberIds.includes(user.id)) || [];

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSubmit = async () => {
    if (selectedUserIds.size === 0) return;

    try {
      await addMembersMutation.mutateAsync({
        groupId,
        memberIds: Array.from(selectedUserIds),
      });
      setIsOpen(false);
      setSelectedUserIds(new Set());
      onSuccess();
    } catch (error) {
      console.error('Error adding members:', error);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
    setSelectedUserIds(new Set());
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
        disabled={addMembersMutation.isPending}
      >
        {t('common.cancel')}
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
        disabled={selectedUserIds.size === 0 || addMembersMutation.isPending}
      >
        {addMembersMutation.isPending ? (
          <CircleNotchIcon size={20} className="animate-spin mx-auto" />
        ) : (
          t('admin.group_detail.add_member_modal.submit', { count: selectedUserIds.size })
        )}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('admin.group_detail.add_member_modal.title')}
      footer={footer}
      maxWidth="lg"
    >
      <div className="flex flex-1 min-h-0 flex-col gap-4">
        <div className="relative flex-shrink-0">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlass size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.group_detail.add_member_modal.search_placeholder')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
        </div>

        {selectedUserIds.size > 0 && (
          <p className="flex-shrink-0 text-sm text-primary font-medium">
            {t('admin.group_detail.add_member_modal.selected', { count: selectedUserIds.size })}
          </p>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 rounded-md">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <CircleNotchIcon size={24} className="animate-spin text-primary" />
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {t('admin.group_detail.add_member_modal.no_users')}
            </div>
          ) : (
            availableUsers.map((user: UserCompanyMemberDto) => (
              <label
                key={user.id}
                className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <input
                  type="checkbox"
                  checked={selectedUserIds.has(user.id)}
                  onChange={() => handleToggleUser(user.id)}
                  className="w-5 h-5 text-primary bg-white border-gray-300 rounded focus:ring-primary"
                />
                <div className="ml-3 flex items-center flex-1">
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-medium">
                    {getInitials(user.firstName, user.lastName)}
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">
                      {user.firstName} {user.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </div>
              </label>
            ))
          )}
        </div>
      </div>
    </BaseModal>
  );
});

AddMemberToGroupModal.displayName = 'AddMemberToGroupModal';
